import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth.jsx";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import NewSiniestro from "@/pages/NewSiniestro";
import NewTelematico from "@/pages/NewTelematico";
import SiniestroDetail from "@/pages/SiniestroDetail";
import CallRoomPerito from "@/pages/CallRoomPerito";
import InsuredSession from "@/pages/InsuredSession";
import ApiDocs from "@/pages/ApiDocs";
import ExpertEntry from "@/pages/ExpertEntry";
import Verify from "@/pages/Verify";
import Team from "@/pages/Team";
import AcceptInvitation from "@/pages/AcceptInvitation";
import ResetPassword from "@/pages/ResetPassword";
import Organizations from "@/pages/Organizations";
import Companies from "@/pages/Companies";
import Preexistencia from "@/pages/Preexistencia";
import ChangePassword from "@/pages/ChangePassword";
import Layout from "@/components/Layout";

// Decodifica el payload del JWT actual (sin verificar firma) para conocer su
// ``scope``. Lo usamos para saber si el perito entró a la app vía iframe del
// ERP (``expert_room``) y, en ese caso, saltarnos el gate de "must change
// password" — el ERP ya autenticó al usuario y forzarle a cambiar la
// contraseña en mitad de un Avizor sería un callejón sin salida (no puede
// hacerlo porque /auth/change-password rechaza expert_room).
function _currentTokenScope() {
  try {
    const t = localStorage.getItem("vp_token");
    if (!t) return null;
    const parts = t.split(".");
    if (parts.length < 2) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload?.scope || null;
  } catch (_) { return null; }
}

function Protected({ children }) {
  const { ready, user } = useAuth();
  if (!ready) return <div className="p-10 text-slate-500">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Si el usuario está marcado para cambio obligatorio de contraseña, le
  // forzamos a esa ruta. EXCEPCIÓN: si entró por Avizor (scope=expert_room),
  // no le interrumpimos.
  if (user.must_change_password && _currentTokenScope() !== "expert_room") {
    return <Navigate to="/cambiar-contrasena" replace />;
  }
  return children;
}

function ForcePwChangeRoute({ children }) {
  const { ready, user } = useAuth();
  if (!ready) return <div className="p-10 text-slate-500">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.must_change_password) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Public asegurado mobile route */}
          <Route path="/s/:token" element={<InsuredSession />} />
          {/* Public certificate verification page */}
          <Route path="/v/:id" element={<Verify />} />
          {/* Public invitation acceptance page */}
          <Route path="/invitacion/:token" element={<AcceptInvitation />} />
          {/* Public reset password page */}
          <Route path="/reset/:token" element={<ResetPassword />} />
          {/* Forced password change (post-login) */}
          <Route path="/cambiar-contrasena" element={<ForcePwChangeRoute><ChangePassword /></ForcePwChangeRoute>} />
          {/* Entrada del perito vía iframe del ERP con JWT temporal */}
          <Route path="/expert/:id" element={<ExpertEntry />} />
          {/* Perito routes */}
          <Route path="/" element={<Protected><Layout><Dashboard /></Layout></Protected>} />
          <Route path="/siniestros/new" element={<Protected><Layout><NewSiniestro /></Layout></Protected>} />
          <Route path="/telematico/new" element={<Protected><Layout><NewTelematico /></Layout></Protected>} />
          <Route path="/siniestros/:id" element={<Protected><Layout><SiniestroDetail /></Layout></Protected>} />
          <Route path="/integracion" element={<Protected><Layout><ApiDocs /></Layout></Protected>} />
          <Route path="/equipo" element={<Protected><Layout><Team /></Layout></Protected>} />
          <Route path="/admin/organizaciones" element={<Protected><Layout><Organizations /></Layout></Protected>} />
          <Route path="/admin/companias" element={<Protected><Layout><Companies /></Layout></Protected>} />
          <Route path="/preexistencia" element={<Protected><Layout><Preexistencia /></Layout></Protected>} />
          <Route path="/call/:id" element={<Protected><CallRoomPerito /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
